import { Alert, cn } from '@h2hspark/ui';
import { X } from 'lucide-react';
import * as React from 'react';

/**
 * The in-app notice — the second of the three notification devices (`MEM-040` AC-1).
 *
 * ## One component, so there is one of these
 *
 * `MEM-NOT-002` allows a badge, a notice, and the surface a notice routes to. The failure mode is
 * not somebody writing a fourth device on purpose; it is six screens each growing their own
 * banner, subtly different, none dismissible in the same way, until the member is being told
 * things in six visual languages. So a notice is this component or it is not a notice, and
 * `the-three-notification-devices.test.ts` scans the member surfaces for a second banner shape.
 *
 * ## Shape
 *
 * One row: an optional icon, the title in bold running into the body, an optional action and an
 * optional dismiss. Dismiss is a real
 * control with an accessible name rather than a bare `×`, because it is often the answer a member
 * wants and a device you cannot turn off is one they learn to resent.
 *
 * `role="alert"` comes from `Alert`. That is right for both of today's callers — losing
 * connectivity and being offered push are both things worth interrupting a screen reader for —
 * and it is worth checking again if a quieter notice ever lands here.
 */
export function InAppNotice({
  icon,
  title,
  body,
  action,
  onDismiss,
  dismissLabel,
  variant,
  className,
}: {
  /** Drawn at the start of the row, hidden from screen readers: the words carry the meaning. */
  readonly icon?: React.ReactNode;
  readonly title: string;
  readonly body: string;
  readonly action?: {
    readonly label: string;
    readonly onSelect: () => void;
    readonly busy?: boolean;
  };
  readonly onDismiss?: () => void;
  readonly dismissLabel?: string;
  readonly variant?: 'default' | 'warning';
  readonly className?: string;
}): React.ReactElement {
  const tone = variant ?? 'default';
  return (
    <Alert
      variant={tone}
      /*
        **One compact row** — TW 44190004, Brad's cleaned design for Your Events.

        A bell, the title in bold running into the body, the action as an underlined link at the
        right and the close control after it, on `--purple-50`. It replaces the white card with a
        title line, a body and a pill button under it (TW 44159932), which answered the same
        person's earlier report that the notice had no fill of its own. The fill is still set here
        rather than on `Alert`, whose other callers sit inside white cards and dialogs.

        Only on the default tone: `warning` carries its own amber, and `cn` merges last-wins, so an
        unconditional fill would quietly take it off the offline notice.

        On the phone the action drops under the words, and the close control stays at the top
        right, where it is on every other dismissible surface.
      */
      className={cn(
        'flex items-start gap-3.5 rounded-2xl px-5 py-3.5 pr-12 md:items-center md:gap-4',
        tone === 'default' && 'border-brand-100 bg-brand-50',
        className,
      )}
    >
      {icon === undefined ? null : (
        <span aria-hidden="true" className="mt-0.5 shrink-0 text-brand md:mt-0">
          {icon}
        </span>
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-2 md:flex-row md:items-center md:gap-6">
        <p className="min-w-0 flex-1 text-sm leading-relaxed text-text-body">
          <strong className="font-bold text-foreground">{title}</strong> {body}
        </p>
        {action === undefined ? null : (
          <button
            type="button"
            disabled={action.busy === true}
            onClick={action.onSelect}
            className="shrink-0 rounded-sm font-brand text-sm font-bold text-brand underline underline-offset-4 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
          >
            {action.label}
          </button>
        )}
      </div>
      {onDismiss === undefined ? null : (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel ?? 'Dismiss'}
          className="absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-pill text-muted-foreground hover:text-foreground md:top-1/2 md:-translate-y-1/2"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}
    </Alert>
  );
}
